import { supabase } from "src/lib/supabaseClient";
import { useProfileStore } from "src/store/profileStore";
import { USER_TYPE, UserType } from "src/context/AuthContext";

/**
 * Service for handling profile data fetching and management
 * Extracted from AuthContext to improve maintainability
 */
export class ProfileService {
  private static isProfileFetching = false;
  private static lastProfileFetchId: string | null = null;

  /**
   * Preload profile images for better UX
   */
  static async preloadProfileImage(imageUrl: string): Promise<void> {
    if (!imageUrl) return;

    try {
      // Extract the path from the full URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');

      // Find the bucket and path - support all storage buckets
      const supportedBuckets = ['student-uploads', 'tutor-uploads', 'admin-uploads', 'avatars'];
      let bucketIndex = -1;

      for (const bucket of supportedBuckets) {
        bucketIndex = pathParts.findIndex(part => part === bucket);
        if (bucketIndex !== -1) break;
      }

      if (bucketIndex === -1) {
        console.log('Skipping preload for non-storage URL:', imageUrl);
        return;
      }

      const bucket = pathParts[bucketIndex];
      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      // Create signed URL for preloading
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.log('Error preloading profile image:', error);
        return;
      }

      if (data?.signedUrl) {
        // Preload the image by creating an Image object
        const img = new Image();
        img.onload = () => console.log('✅ Profile image preloaded successfully');
        img.onerror = () => console.log('❌ Profile image preload failed');
        img.src = data.signedUrl;
      }
    } catch (error) {
      console.log('Error preloading profile image:', error);
    }
  }

  /**
   * Fetch complete tutor profile data in a single call
   */
  static async fetchCompleteTutorProfile(userId: string, explicitUserType?: string): Promise<any> {
    console.log('fetchCompleteTutorProfile called for:', userId);

    if (!userId) {
      console.log('No userId provided');
      return null;
    }

    // Prevent duplicate calls for the same user
    if (this.isProfileFetching && this.lastProfileFetchId === userId) {
      console.log('Already fetching complete tutor profile for this user, skipping duplicate call');
      return null;
    }

    try {
      this.isProfileFetching = true;
      this.lastProfileFetchId = userId;

      // Fetch basic profile data
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching basic profile:', profileError);
        return null;
      }

      // Fetch tutor-specific data from tutors table
      const { data: tutorData, error: tutorError } = await supabase
        .from("tutors")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      let candidateTutorData = null;
      if (tutorError) {
        console.log('No tutor record found in tutors table, trying candidate_tutor table:', tutorError);

        // Fallback to candidate_tutor table if tutors table doesn't have the record
        const { data: candidateData, error: candidateError } = await supabase
          .from("candidate_tutor")
          .select("*")
          .eq("id", userId)
          .maybeSingle();

        if (candidateError) {
          console.error('Error fetching tutor data from both tables:', candidateError);
          // Continue with basic profile data only
        } else {
          console.log('Found tutor data in candidate_tutor table');
          candidateTutorData = candidateData;
        }
      }

      console.log('Complete tutor profile data fetched:', {
        profileData,
        tutorData: tutorData || candidateTutorData
      });

      // Update profile data in the profile store with comprehensive tutor data
      const storeProfileData = {
        firstName: profileData.first_name,
        lastName: profileData.last_name,
        email: profileData.email,
        userType: explicitUserType || USER_TYPE.TUTOR,
        profilePictureUrl: profileData.profile_picture_url,
        timezone: profileData.timezone,
        createdAt: profileData.created_at ? new Date(profileData.created_at) : null,
        updatedAt: profileData.updated_at ? new Date(profileData.updated_at) : null,

        // Tutor-specific data
        education: (tutorData || candidateTutorData)?.education || [],
        hourlyRate: (tutorData || candidateTutorData)?.hourly_rate || 0,
        subjectsTaught: (tutorData || candidateTutorData)?.subjects_taught || [],
        experience: (tutorData || candidateTutorData)?.experience || [],
        bio: (tutorData || candidateTutorData)?.bio || "",
        availability: (tutorData || candidateTutorData)?.availability || {},
        verificationStatus: (tutorData || candidateTutorData)?.verification_status || "",
        cvFilePath: (tutorData || candidateTutorData)?.cv_file_path || "",
        rating: (tutorData || candidateTutorData)?.rating || 0,
        dateOfBirth: (tutorData || candidateTutorData)?.date_of_birth || null,

        // New enhanced tutor fields
        achievements: (tutorData || candidateTutorData)?.achievements || [],
        expertise: (tutorData || candidateTutorData)?.expertise || [],

        // Availability slots (empty for now since tutor_availability table doesn't exist)
        availabilitySlots: [],

        // Additional tutor fields that might be useful
        specializations: (tutorData || candidateTutorData)?.specializations || [],
        subjects: Array.isArray((tutorData || candidateTutorData)?.subjects_taught) 
          ? (tutorData || candidateTutorData).subjects_taught 
          : (tutorData || candidateTutorData)?.subjects_taught 
            ? [(tutorData || candidateTutorData).subjects_taught]
            : [],
      };

      // Update the profile store with complete tutor data
      console.log('fetchCompleteTutorProfile: Setting profile data with userType:', storeProfileData.userType);
      useProfileStore.getState().setProfileData(storeProfileData);

      // Preload profile image if available to improve UX
      if (profileData.profile_picture_url) {
        this.preloadProfileImage(profileData.profile_picture_url);
      }

      console.log('Updated tutor profile data in store:', storeProfileData);

      return {
        ...profileData,
        ...(tutorData || candidateTutorData)
      };

    } catch (error) {
      console.error('Error in fetchCompleteTutorProfile:', error);
      return null;
    } finally {
      this.isProfileFetching = false;
    }
  }

  /**
   * Fetch complete student profile data in a single call
   */
  static async fetchCompleteStudentProfile(userId: string, explicitUserType?: string): Promise<any> {
    console.log('fetchCompleteStudentProfile called for:', userId);

    if (!userId) {
      console.log('No userId provided');
      return null;
    }

    // Prevent duplicate calls for the same user
    if (this.isProfileFetching && this.lastProfileFetchId === userId) {
      console.log('Already fetching complete profile for this user, skipping duplicate call');
      return null;
    }

    try {
      this.isProfileFetching = true;
      this.lastProfileFetchId = userId;

      // First try the complete profile function
      let { data, error } = await supabase.rpc('get_student_complete_profile', {
        student_id: userId
      });

      // If there's a timestamp error, fall back to basic profile function
      if (error && error.message?.includes('timestamp')) {
        console.log('Timestamp error detected, falling back to basic profile function:', error);

        const { data: basicData, error: basicError } = await supabase.rpc('get_basic_student_profile', {
          student_id: userId
        });

        if (basicError) {
          console.error('Error fetching basic student profile:', basicError);
          return null;
        }

        // Use basic data and set defaults for missing fields
        data = basicData?.map((profile: any) => ({
          ...profile,
          subjects_of_interest: [],
          learning_goals: [],
          study_preferences: {},
          academic_history: {},
          hobbies: [],
          interests: [],
          location: '',
          date_of_birth: null,
          active_subscriptions_count: 0,
          active_subscriptions: [],
          all_subscriptions: [],
          earliest_subscription_end: null,
          latest_subscription_end: null,
          total_days_remaining: 0
        }));

        error = null; // Clear the error since we handled it
      }

      if (error) {
        console.error('Error fetching complete student profile:', error);
        return null;
      }

      console.log('Complete student profile data:', data);

      // RPC functions return arrays, so get the first element
      const profileData = data && data.length > 0 ? data[0] : null;

      if (profileData) {
        // Update profile data in the profile store
        const storeProfileData = {
          id: profileData.id,
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          email: profileData.email,
          userType: explicitUserType || USER_TYPE.STUDENT,
          profilePictureUrl: profileData.profile_picture_url,
          timezone: profileData.timezone,
          createdAt: profileData.created_at ? new Date(profileData.created_at) : null,
          updatedAt: profileData.updated_at ? new Date(profileData.updated_at) : null,
          // Student-specific data (camelCase only)
          educationLevel: profileData.education_level,
          subjectsOfInterest: profileData.subjects_of_interest || [],
          learningGoals: profileData.learning_goals || [],
          studyPreferences: profileData.study_preferences || {},
          academicHistory: profileData.academic_history || {},
          hobbies: profileData.hobbies || [],
          interests: profileData.interests || [],
          location: profileData.location,
          dateOfBirth: profileData.date_of_birth,
          profileCompleteness: 0,
          socialLinks: {},
          isEnrolled: profileData.is_enrolled || false,
          activeSubscriptionsCount: profileData.active_subscriptions_count || 0,
          activeSubscriptions: profileData.active_subscriptions || [],
          allSubscriptions: profileData.all_subscriptions || [],
          totalDaysRemaining: profileData.total_days_remaining || 0
        };

        // Update the profile store with complete data
        console.log('fetchCompleteStudentProfile: Setting profile data with userType:', storeProfileData.userType);
        useProfileStore.getState().setProfileData(storeProfileData);

        // Preload profile image if available to improve UX
        if (profileData.profile_picture_url) {
          this.preloadProfileImage(profileData.profile_picture_url);
        }

        console.log('Updated profile and enrollment status:', {
          isEnrolled: profileData.is_enrolled,
          activeSubscriptions: profileData.active_subscriptions_count,
          profileCompleteness: profileData.profile_completeness
        });

        return profileData;
      }

      return null;
    } catch (error) {
      console.error('Error in fetchCompleteStudentProfile:', error);
      return null;
    } finally {
      this.isProfileFetching = false;
    }
  }

  /**
   * Reset profile fetching state (useful for cleanup)
   */
  static resetFetchingState(): void {
    this.isProfileFetching = false;
    this.lastProfileFetchId = null;
  }
}